import { ClassValue } from "clsx";
import { HTMLAttributeAnchorTarget, InputHTMLAttributes, ReactNode } from "react";
import { IconProps } from "@/components/library/Icon";
import { InputLabelProps } from "@/components/library/InputLabel";
import { Permissions, RoleData } from "@/enums/user";

export type GenericComponentTypes = {
  ["data-testid"]?: string;
  className?: ClassValue;
} & Status;

export type Status = {
  error?: boolean;
  success?: boolean;
  warning?: boolean;
};

export type GenericInputTypes<T = HTMLInputElement> = {
  clean?: boolean;
  clearable?: boolean;
  label?: InputLabelProps["value"];
  helperText?: ReactNode;
} & InputHTMLAttributes<T> &
  GenericComponentTypes;

type NavItemAccess = {
  dataType: RoleData;
  /** Extra data types that also make this item visible; see `RouteEntry.nav`. */
  additionalDataTypes?: RoleData[];
  /** CRUD levels that grant visibility; omitted means Read. Any one level is enough. */
  levels?: readonly Permissions[];
};

export type GlobalNavItemType =
  | {
      id?: string;
      icon: IconProps["icon"];
      label: string;
      href: string;
      target?: HTMLAttributeAnchorTarget;
      onClick?: never;
      isActive?: boolean;
      subLinks?: never;
      dataAttributes?: Record<string, string>;
      /** Searchable tags/keywords for navigation search */
      tags?: string[];
      /** Whether to show a notification indicator on this item */
      hasAlert?: boolean;
      /** Visual treatment; "assistant" gets the brand-accented styling. */
      variant?: "assistant";
      /** Keyboard shortcut hint rendered at the right edge (e.g. "⌘G"). */
      shortcutHint?: string;
    } & NavItemAccess
  | {
      id?: string;
      icon: IconProps["icon"];
      label: string;
      href?: never;
      target?: never;
      onClick?: never;
      isActive?: boolean;
      subLinks: GlobalNavItemType[];
      dataAttributes?: never;
      /** Searchable tags/keywords for navigation search */
      tags?: string[];
      /** Whether to show a notification indicator on this item */
      hasAlert?: boolean;
      /** Visual treatment; "assistant" gets the brand-accented styling. */
      variant?: "assistant";
      /** Keyboard shortcut hint rendered at the right edge (e.g. "⌘G"). */
      shortcutHint?: string;
    } & NavItemAccess
  | {
      /** Action item: runs a callback instead of navigating (e.g. open the assistant dock). */
      id?: string;
      icon: IconProps["icon"];
      label: string;
      href?: never;
      target?: never;
      onClick: () => void;
      isActive?: boolean;
      subLinks?: never;
      dataAttributes?: Record<string, string>;
      /** Searchable tags/keywords for navigation search */
      tags?: string[];
      /** Whether to show a notification indicator on this item */
      hasAlert?: boolean;
      /** Visual treatment; "assistant" gets the brand-accented styling. */
      variant?: "assistant";
      /** Keyboard shortcut hint rendered at the right edge (e.g. "⌘G"). */
      shortcutHint?: string;
    } & NavItemAccess;

export type GlobalNavSectionType = {
  name?: string;
  sectionKey?: string;
  items: GlobalNavItemType[];
  position: "top" | "bottom";
};

export const SIDENAV_FADE_DURATION = 400;
